import { BrandKicker, PulseTrace } from '../../components/brand';
import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Seo from '../../components/seo/Seo';
import {
  Layout,
  HeroSection,
  HeroTitle,
  HeroSubtitle,
  PageContainer,
  Section,
  SectionTitle,
  SectionText,
  BreadcrumbLink,
  List,
  ListItem,
  ProcessList,
  ProcessStep,
  ShowcaseGrid,
  ShowcaseCard,
  ShowcaseTitle,
  ShowcaseText,
  Callout,
  CalloutTitle,
  CalloutText,
  CalloutLink,
  CtaSection,
  CtaTitle,
  CtaText,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  TestimonialCard,
  TestimonialQuote,
  TestimonialSource,
} from './Style';
import {
  getServiceBySlug,
  getServicePath,
  getServicesPath,
} from './servicesConfig';

type ServiceSection = {
  title: string;
  items?: string[];
  text?: string;
};

type ProcessData = {
  title: string;
  steps: string[];
};

type TestimonialData = {
  quote: string;
  source: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const ServiceDetail = () => {
  const { lang: langParam, slug } = useParams();
  const { t, i18n } = useTranslation(['services', 'ourWork']);
  const lang = langParam === 'fr' ? 'fr' : 'en';
  const siteUrl =
    (import.meta as any).env?.VITE_SITE_URL || 'https://sor-seo.com';
  const homePath = lang === 'fr' ? '/fr' : '/';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, lang]);

  const service = getServiceBySlug(slug ?? '');

  if (!service) {
    return <Navigate to={getServicesPath(lang)} replace />;
  }

  if (slug !== service.slugs[lang]) {
    return <Navigate to={getServicePath(lang, service.slugs[lang])} replace />;
  }

  const detailKey = `detail.${service.id}`;
  const serviceTitle = t(`${detailKey}.hero.title`);
  const serviceIntro = t(`${detailKey}.intro`);
  const sections = t(`${detailKey}.sections`, { returnObjects: true });
  const normalizedSections = Array.isArray(sections)
    ? (sections as ServiceSection[])
    : [];

  const process = t(`${detailKey}.process`, { returnObjects: true });
  const processData = isRecord(process) ? (process as ProcessData) : null;

  const useCases = t(`${detailKey}.useCases`, { returnObjects: true });
  const normalizedUseCases = Array.isArray(useCases)
    ? (useCases as string[])
    : [];

  const testimonial = t(`${detailKey}.testimonial`, { returnObjects: true });
  const testimonialData = isRecord(testimonial)
    ? (testimonial as TestimonialData)
    : null;

  const englishProjects =
    i18n.getResourceBundle('en', 'ourWork')?.projects || {};
  const localizedProjects =
    i18n.getResourceBundle(lang, 'ourWork')?.projects || {};
  const relatedProjects = service.projects.flatMap((slug) => {
    const match = Object.entries(englishProjects).find(
      ([key, project]) => key === slug || (project as any).slug === slug,
    );
    const project = match ? localizedProjects[match[0]] : null;
    return project ? [project] : [];
  });

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SOR SEO',
    url: siteUrl,
    logo: `${siteUrl}/logoSORBlack.svg`,
    sameAs: ['https://www.linkedin.com/company/sor-seo'],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceTitle,
    description: serviceIntro,
    provider: {
      '@type': 'Organization',
      name: 'SOR SEO',
      url: siteUrl,
    },
    serviceType: serviceTitle,
    areaServed: 'CA',
    url: `${siteUrl}${getServicePath(lang, service.slugs[lang])}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('navigation.homeLabel'),
        item: `${siteUrl}${homePath}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('hero.title'),
        item: `${siteUrl}${getServicesPath(lang)}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: serviceTitle,
        item: `${siteUrl}${getServicePath(lang, service.slugs[lang])}`,
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title={t(`${detailKey}.seoTitle`)}
        description={t(`${detailKey}.seoDescription`)}
        canonicalPath={getServicePath(lang, service.slugs[lang])}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <HeroSection>
        <BrandKicker>
          SOR SEO /{' '}
          {lang === 'fr' ? 'L’expertise en action' : 'Expertise in action'}
        </BrandKicker>
        <HeroTitle>{serviceTitle}</HeroTitle>
        <HeroSubtitle>{t(`${detailKey}.hero.subtitle`)}</HeroSubtitle>
        <PulseTrace />
      </HeroSection>
      <PageContainer>
        <BreadcrumbLink to={getServicesPath(lang)}>
          {t('navigation.backToServices')}
        </BreadcrumbLink>

        <Section>
          <SectionText>{serviceIntro}</SectionText>
        </Section>

        {normalizedSections.map((section) => (
          <Section key={section.title}>
            <SectionTitle>{section.title}</SectionTitle>
            {section.text ? <SectionText>{section.text}</SectionText> : null}
            {Array.isArray(section.items) ? (
              <List>
                {section.items.map((item) => (
                  <ListItem key={item}>{item}</ListItem>
                ))}
              </List>
            ) : null}
          </Section>
        ))}

        {processData?.steps?.length ? (
          <Section>
            <SectionTitle>{processData.title}</SectionTitle>
            <ProcessList>
              {processData.steps.map((step) => (
                <ProcessStep key={step}>{step}</ProcessStep>
              ))}
            </ProcessList>
          </Section>
        ) : null}

        {normalizedUseCases.length ? (
          <Section>
            <SectionTitle>{t(`${detailKey}.useCasesTitle`)}</SectionTitle>
            <List>
              {normalizedUseCases.map((item) => (
                <ListItem key={item}>{item}</ListItem>
              ))}
            </List>
          </Section>
        ) : null}

        {testimonialData?.quote ? (
          <Section>
            <TestimonialCard>
              <TestimonialQuote>{testimonialData.quote}</TestimonialQuote>
              <TestimonialSource>{testimonialData.source}</TestimonialSource>
            </TestimonialCard>
          </Section>
        ) : null}

        {relatedProjects.length ? (
          <Section>
            <SectionTitle>{t('navigation.showcaseTitle')}</SectionTitle>
            <SectionText>{t('navigation.showcaseSubtitle')}</SectionText>
            <ShowcaseGrid>
              {relatedProjects.map((project) => (
                <ShowcaseCard key={project.slug} to={project.internalLink}>
                  <ShowcaseTitle>{project.title}</ShowcaseTitle>
                  <ShowcaseText>{project.title2}</ShowcaseText>
                </ShowcaseCard>
              ))}
            </ShowcaseGrid>
          </Section>
        ) : null}

        {service.id === 'marketingGrowth' ? (
          <Callout>
            <CalloutTitle>{t(`${detailKey}.abmCallout.title`)}</CalloutTitle>
            <CalloutText>{t(`${detailKey}.abmCallout.text`)}</CalloutText>
            <CalloutLink to={`/${lang}/account-based-marketing`}>
              {t(`${detailKey}.abmCallout.linkLabel`)}
            </CalloutLink>
          </Callout>
        ) : null}

        <CtaSection>
          <CtaTitle>{t('detailCta.title')}</CtaTitle>
          <CtaText>{t('detailCta.description')}</CtaText>
          <ButtonRow>
            <PrimaryButton to={`/${lang}/contact`}>
              {t('detailCta.primaryLabel')}
            </PrimaryButton>
            <SecondaryButton to={getServicesPath(lang)}>
              {t('detailCta.secondaryLabel')}
            </SecondaryButton>
          </ButtonRow>
        </CtaSection>
      </PageContainer>
    </Layout>
  );
};

export default ServiceDetail;
